import React, {
  createRef, useState, ChangeEvent, RefObject, useEffect
} from 'react'
import useDraft from '../../../redux/draft-drawer/useDraft'
import {
  IconInstagramOutline,
  IconFacebookSolid,
  IconLinkedInSolid,
  IconPinterestSolid,
  IconYouTubeSolid
} from '../../Icon/Brands'

export const useDraftDrawerCaption = (scrollRef: RefObject<HTMLDivElement>) => {
  const {
    characterCountType,
    captionText,
    actions: { setDraftCaption, changeCharacterCountType }
  } = useDraft()
  const [pickerOpen, setPickerOpen] = useState(false)
  const textareaRef = createRef<HTMLTextAreaElement>()

  // Resizes the textarea and controls the scrolled amount of its container
  // to ensure that the user doesn't loose track of where they are typing
  const handleSizing = (textarea: HTMLTextAreaElement) => {
    const parentElement = textarea.parentNode as HTMLElement
    parentElement.dataset.replicatedValue = textarea.value

    if (!scrollRef.current) {
      return
    }

    const scrollElement = scrollRef.current
    const scrolledAmount = scrollElement.scrollTop
    const scrollHeight = scrollElement.scrollHeight - scrollElement.offsetHeight
    const scrollThreshold = 50

    if (scrolledAmount < scrollHeight - scrollThreshold) {
      return
    }

    scrollElement.scrollTo({ top: scrollElement.scrollHeight })
  }

  // Handles the placement of the Emoji in the textarea
  const handleEmoji = (emoji?: string) => {
    const textarea = textareaRef.current

    if (!textarea || !emoji) {
      return
    }

    const textValue = textarea.value
    const caretPos = textarea.selectionEnd
    const newCaretPos = caretPos + emoji.length
    const newValue = textValue.slice(0, caretPos) + emoji + textValue.slice(caretPos)

    textarea.value = newValue
    textarea.focus()
    textarea.selectionEnd = newCaretPos

    setDraftCaption(newValue)
    setPickerOpen(false)
  }

  const getCharacterCountIcon = () => {
    switch (characterCountType) {
      case 'instagram':
        return <IconInstagramOutline size={18} className="social-icon" />
      case 'facebook':
        return <IconFacebookSolid size={18} className="social-icon" />
      case 'linkedin':
        return <IconLinkedInSolid size={18} className="social-icon" />
      case 'pinterest':
        return <IconPinterestSolid size={18} className="social-icon" />
      case 'youtube':
        return <IconYouTubeSolid size={18} className="social-icon" />
    }
  }

  const getCharacterCountLimit = () => {
    switch (characterCountType) {
      case 'instagram':
        return 2200
      case 'facebook':
        return 63206
      case 'linkedin':
        return 3000
      case 'pinterest':
        return 500
      case 'youtube':
        return 5000
    }
  }

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setDraftCaption(e.currentTarget.value)
    handleSizing(e.currentTarget)
  }

  useEffect(() => {
    if (textareaRef.current) {
      handleSizing(textareaRef.current)
    }
  }, [textareaRef])

  return {
    captionText,
    textareaRef,
    pickerOpen,
    actions: {
      handleChange,
      handleEmoji,
      getCharacterCountIcon,
      getCharacterCountLimit,
      changeCharacterCountType,
      setPickerOpen
    }
  }
}
